import { Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { BusConfig } from '../config/bus.config';
import type { EventBus } from '../core/EventBus';
import type { GameEvents } from '../core/GameEvents';
import type { SeatPose } from '../dozer/DozerPresenter';
import type { TrackManager } from '../level/TrackManager';
import { trackFrame } from '../level/TrackManager';
import type { EntityVisual } from '../rendering/EntityVisual';
import type { VisualPool } from '../rendering/VisualPool';
import { clamp, damp, DEG_TO_RAD, lerp, TAU } from '../utils/math';
import { BusCrash, type BusManager } from './BusManager';
import { BusPhase, type BusRide } from './BusRide';

const ASSET = 'VEH_BUS_A';
const NODE = { body: 'VEH_BUS_A_PIVOT_BODY', wheel: 'VEH_BUS_A_PIVOT_WHEEL_', cargo: 'VEH_BUS_A_PIVOT_CARGO_' } as const;
/** A piece of load thrown off the roof (wood bundle with straps). */
const CARGO_ASSET = 'ENV_BUS_CARGO_A';
/** A lost piece lies on the road until it is this far behind the runner (m). */
const CARGO_BEHIND = 50;
/** Driver's seat in vehicle space (left-hand drive: the driver sits left of the centre line). */
const DRIVER_X = -0.38;
/** Half the track width of the van (m) – it tips about its outer wheels. */
const HALF_TRACK = 0.68;
/** The empty van is released this far behind the runner (m). */
const RELEASE_BEHIND = 40;

interface RigNode {
  readonly node: TransformNode;
  readonly position: Vector3;
  readonly rotation: Quaternion | null;
}

/** One spring-damper degree of freedom of the sprung body (roll, pitch, heave). */
interface Spring {
  value: number;
  velocity: number;
}

/** A piece of load in flight / on the road (track space). */
interface FlyingCargo {
  readonly visual: EntityVisual;
  distance: number;
  x: number;
  y: number;
  vd: number;
  vx: number;
  vy: number;
  pitch: number;
  roll: number;
  spin: number;
  resting: boolean;
}

interface Entry {
  readonly ride: BusRide;
  readonly visual: EntityVisual;
  readonly body: RigNode | null;
  readonly wheels: readonly { readonly rig: RigNode; readonly front: boolean }[];
  /** Load pieces on the rack (VEH_BUS_A_PIVOT_CARGO_1 … n) – piece k shows while the ride has ≥ k left. */
  readonly cargo: readonly RigNode[];
  roll: Spring;
  pitch: Spring;
  heave: Spring;
  /** Lateral speed and forward speed of the last frame (accelerations drive the body). */
  lateral: number;
  forward: number;
  yaw: number;
  steer: number;
  /** Nose pitch of the whole van in the air (rad). */
  airPitch: number;
}

/**
 * Visual side of the ructec-Bulli (BusManager is the gameplay): the van follows the ride between fixed steps along the
 * track (curves, crossings), points into its lane changes, its wheels roll and the front wheels steer. The body sits on
 * springs (BusPresentationConfig.suspension, an under-damped spring-damper per axis): it rolls out of a lane change –
 * the harder the van swerves at speed, the further – and beyond `roll.tipFrom` the inner wheels lift (it tips onto two
 * wheels, never over); it squats when it speeds up, flies nose-up off a ramp and dips into the springs on touch-down.
 * Presentation only – nothing here affects gameplay.
 */
export class BusPresenter {
  private entry: Entry | null = null;
  private readonly flying: FlyingCargo[] = [];
  private readonly frame = trackFrame();
  private readonly seat: SeatPose = { position: new Vector3(), fallback: new Vector3(), yaw: 0 };
  private readonly scratch = new Vector3();
  private readonly turn = new Quaternion();
  private time = 0;

  constructor(
    private readonly pool: VisualPool,
    private readonly track: TrackManager,
    private readonly buses: BusManager,
    private readonly config: BusConfig,
    /** Fixed simulation step (s): lateral speed between two steps. */
    private readonly fixedStep: number,
    /** Track distance of the runner – the empty van is released behind it. */
    private readonly playerDistance: () => number,
    events: EventBus<GameEvents>,
  ) {
    events.on('bus:landed', ({ impactSpeed }) => this.kick(impactSpeed));
    events.on('bus:crashed', ({ ride, car, kind }) => {
      const entry = this.entry;
      if (!entry) return;
      if (kind === BusCrash.HeadOn) entry.pitch.velocity -= 0.9; // the van shudders back from the impact
      // a side crash throws the soft body over, away from the hit (a positive roll leans to −x)
      else entry.roll.velocity += Math.sign(car.pose.x - ride.pose.x) * this.config.presentation.crashRoll * DEG_TO_RAD;
    });
    events.on('bus:cargoLost', ({ ride, pieces }) => this.throwCargo(ride, pieces));
  }

  /** The driver's seat this frame (after render) while the runner drives, or null. */
  seatPose(): Readonly<SeatPose> | null {
    return this.entry && this.buses.active === this.entry.ride ? this.seat : null;
  }

  render(alpha: number, dt: number, paused: boolean): void {
    const step = paused ? 0 : dt;
    this.time += step;
    this.updateFlying(step);
    const ride = this.buses.current;
    if (this.entry && (this.entry.ride !== ride || this.gone(this.entry.ride))) this.release();
    if (!ride || this.gone(ride)) return;
    this.entry ??= this.create(ride);
    this.update(this.entry, alpha, step);
    if (this.buses.active === ride) this.updateSeat(this.entry);
  }

  reset(): void {
    this.release();
    for (const piece of this.flying) this.pool.release(piece.visual);
    this.flying.length = 0;
  }

  // ── Per frame ────────────────────────────────────────────────────────────

  private update(entry: Entry, alpha: number, dt: number): void {
    const { ride } = entry;
    const p = this.config.presentation;
    const distance = lerp(ride.previous.distance, ride.pose.distance, alpha);
    const x = lerp(ride.previous.x, ride.pose.x, alpha);
    const y = lerp(ride.previous.y, ride.pose.y, alpha);
    const travelled = lerp(ride.previous.travelled, ride.pose.travelled, alpha);

    // Speeds and accelerations (smoothed – they drive the springs)
    const lateral = (ride.pose.x - ride.previous.x) / this.fixedStep;
    const forward = ride.pose.speed;
    const lateralAccel = dt > 0 ? (lateral - entry.lateral) / dt : 0;
    const forwardAccel = dt > 0 ? (forward - entry.forward) / dt : 0;
    entry.lateral = damp(entry.lateral, lateral, 18, dt);
    entry.forward = damp(entry.forward, forward, 10, dt);

    // Springs: roll out of the swerve (outward of the acceleration), squat on acceleration, heave settles at 0
    const roll = clamp(-lateralAccel * p.roll.perAccel, -p.roll.max, p.roll.max) * DEG_TO_RAD;
    const pitch = clamp(-forwardAccel * p.pitch.perAccel, -p.pitch.max, p.pitch.max) * DEG_TO_RAD;
    this.spring(entry.roll, ride.pose.airborne ? 0 : roll, dt);
    this.spring(entry.pitch, ride.pose.airborne ? 0 : pitch, dt);
    this.spring(entry.heave, 0, dt);

    // The whole van: along the track, into the lane change, nose-up off a ramp (eases down in the air)
    const maxYaw = p.yaw.max * DEG_TO_RAD;
    entry.yaw = damp(entry.yaw, clamp(entry.lateral * p.yaw.perLateral * DEG_TO_RAD, -maxYaw, maxYaw), 12, dt);
    const slope = ride.pose.airborne ? 0 : Math.atan2(ride.pose.y - ride.previous.y, Math.max(0.01, ride.pose.distance - ride.previous.distance));
    entry.airPitch = ride.pose.airborne ? damp(entry.airPitch, -0.06, 1.6, dt) : damp(entry.airPitch, slope, 14, dt);

    // Tipping: beyond tipFrom the inner wheels lift – the van turns about its outer wheels (never over)
    const rollDeg = Math.abs(entry.roll.value) / DEG_TO_RAD;
    let tip = clamp((rollDeg - p.roll.tipFrom) / (p.roll.max - p.roll.tipFrom), 0, 1) * p.roll.tipLiftMax * DEG_TO_RAD;
    let tipSign = Math.sign(entry.roll.value);
    // tipped over by the rival: on its side, back onto its wheels, then it blinks (BusManager.tipped)
    const fall = this.fallAngle();
    if (fall.angle > 0) {
      tip = fall.angle;
      tipSign = fall.roll;
    }
    entry.visual.root.setEnabled(!fall.hidden);

    const speedShare = clamp(forward / 20, 0, 1);
    const rumble = p.rumble.amplitude * speedShare * Math.sin(TAU * p.rumble.hz * this.time) * (ride.pose.airborne ? 0 : 1);
    this.track.trackToWorld(x, y + rumble + HALF_TRACK * Math.sin(tip), distance, this.frame);
    const root = entry.visual.root;
    root.position.set(this.frame.x, this.frame.y, this.frame.z);
    root.rotationQuaternion = null;
    // Babylon: a positive pitch lowers +z (nose down) – the climb raises the nose; a positive roll leans to −x
    root.rotation.set(-entry.airPitch, this.frame.yaw + entry.yaw, tipSign * tip);

    // Sprung body on the wheels
    const body = entry.body;
    if (body) {
      body.node.position.copyFrom(body.position);
      body.node.position.y += entry.heave.value;
      Quaternion.RotationYawPitchRollToRef(0, entry.pitch.value, entry.roll.value, this.turn);
      body.node.rotationQuaternion ??= new Quaternion();
      if (body.rotation) body.rotation.multiplyToRef(this.turn, body.node.rotationQuaternion);
      else body.node.rotationQuaternion.copyFrom(this.turn);
    }

    // Load on the rack: what was lost is gone
    entry.cargo.forEach((rig, i) => rig.node.setEnabled(i < ride.cargo));

    // Wheels: roll with the distance, the front ones steer into the lane change
    const maxSteer = p.steer.max * DEG_TO_RAD;
    entry.steer = damp(entry.steer, clamp(entry.lateral * p.steer.perLateral * DEG_TO_RAD, -maxSteer, maxSteer), 16, dt);
    const spin = travelled / p.wheelRadius;
    for (const wheel of entry.wheels) {
      Quaternion.RotationYawPitchRollToRef(wheel.front ? entry.steer : 0, spin, 0, this.turn);
      wheel.rig.node.rotationQuaternion ??= new Quaternion();
      if (wheel.rig.rotation) wheel.rig.rotation.multiplyToRef(this.turn, wheel.rig.node.rotationQuaternion);
      else wheel.rig.node.rotationQuaternion.copyFrom(this.turn);
    }
  }

  /**
   * The fall after being shoved off the road: up onto its side in 0.35 s (85°), lying until 0.9 s, back onto its wheels
   * by 1.3 s – then it blinks (hidden every other 0.1 s) until the blinking is over.
   */
  private fallAngle(): { angle: number; roll: number; hidden: boolean } {
    const { remaining, side } = this.buses.tipped;
    if (remaining <= 0) return { angle: 0, roll: 0, hidden: false };
    const t = this.config.tip.seconds - remaining;
    const full = 85 * DEG_TO_RAD;
    const angle = t < 0.35 ? full * (t / 0.35) : t < 0.9 ? full : t < 1.3 ? full * (1 - (t - 0.9) / 0.4) : 0;
    // a positive roll leans to −x: shoved to the left (side −1) it falls to the left
    return { angle, roll: -side, hidden: t >= 1.3 && Math.floor(t * 10) % 2 === 0 };
  }

  /** Under-damped spring-damper towards `target` (BusPresentationConfig.suspension). */
  private spring(s: Spring, target: number, dt: number): void {
    if (dt <= 0) return;
    const omega = TAU * this.config.presentation.suspension.hz;
    const accel = omega * omega * (target - s.value) - 2 * this.config.presentation.suspension.damping * omega * s.velocity;
    s.velocity += accel * dt;
    s.value += s.velocity * dt;
  }

  /** Touch-down: the body sinks into the springs and the nose dips – harder the faster it came down. */
  private kick(impactSpeed: number): void {
    const entry = this.entry;
    if (!entry) return;
    const p = this.config.presentation;
    entry.heave.velocity -= p.landingSink * impactSpeed * TAU * p.suspension.hz;
    entry.pitch.velocity += p.pitch.landingDip * DEG_TO_RAD * Math.min(1, impactSpeed / 6) * TAU * p.suspension.hz;
  }

  private updateSeat(entry: Entry): void {
    const root = entry.visual.root;
    root.computeWorldMatrix(true);
    const world = root.getWorldMatrix();
    const { y, z } = this.config.seat;
    Vector3.TransformCoordinatesFromFloatsToRef(DRIVER_X, y, z, world, this.seat.position);
    this.seat.fallback.copyFrom(this.seat.position);
    Vector3.TransformNormalFromFloatsToRef(0, 0, 1, world, this.scratch);
    this.seat.yaw = Math.atan2(this.scratch.x, this.scratch.z);
  }

  // ── Lost load ────────────────────────────────────────────────────────────

  /** `pieces` left the rack (the top ones, ride.cargo is already lowered): each flies off back and up, spinning. */
  private throwCargo(ride: BusRide, pieces: number): void {
    const entry = this.entry;
    const t = this.config.presentation.cargoThrow;
    const pose = ride.pose;
    for (let i = ride.cargo; i < ride.cargo + pieces; i++) {
      const rig = entry?.cargo[i];
      const lx = (entry?.body?.position.x ?? 0) + (rig?.position.x ?? 0);
      const ly = (entry?.body?.position.y ?? 0) + (rig?.position.y ?? 2.1);
      const lz = (entry?.body?.position.z ?? 0) + (rig?.position.z ?? 0);
      const side = Math.sin(i * 2.4 + pose.distance) * t.side;
      this.flying.push({
        visual: this.pool.acquire(CARGO_ASSET, null),
        distance: pose.distance + lz,
        x: pose.x + lx,
        y: pose.y + ly,
        vd: Math.max(0, pose.speed - t.back),
        vx: side,
        vy: t.up,
        pitch: 0,
        roll: 0,
        spin: t.spin * DEG_TO_RAD * (i % 2 === 0 ? 1 : -1),
        resting: false,
      });
    }
  }

  /** Flight with gravity, a skid on the road, then it lies there until the runner is far past it. */
  private updateFlying(dt: number): void {
    const behind = this.playerDistance() - CARGO_BEHIND;
    for (let i = this.flying.length - 1; i >= 0; i--) {
      const piece = this.flying[i]!;
      if (piece.distance < behind) {
        this.pool.release(piece.visual);
        this.flying.splice(i, 1);
        continue;
      }
      if (!piece.resting && dt > 0) {
        piece.vy -= this.config.gravity * dt;
        piece.distance += piece.vd * dt;
        piece.x += piece.vx * dt;
        piece.y += piece.vy * dt;
        piece.pitch += piece.spin * dt;
        piece.roll += piece.spin * 0.4 * dt;
        const ground = this.track.groundHeightAt(piece.distance, piece.x) ?? 0;
        if (piece.y <= ground) {
          // it hits the road: a short bounce, then it skids to a halt lying flat
          piece.y = ground;
          piece.vy = Math.abs(piece.vy) > 2 ? -piece.vy * 0.25 : 0;
          piece.vd = Math.max(0, piece.vd - 30 * dt);
          piece.vx *= 0.8;
          piece.spin *= 0.5;
          if (piece.vy === 0) {
            piece.pitch = Math.round(piece.pitch / Math.PI) * Math.PI;
            piece.roll = 0;
            if (piece.vd <= 0.05) piece.resting = true;
          }
        }
      }
      this.track.trackToWorld(piece.x, piece.y, piece.distance, this.frame);
      const root = piece.visual.root;
      root.position.set(this.frame.x, this.frame.y, this.frame.z);
      root.rotationQuaternion = null;
      root.rotation.set(piece.pitch, this.frame.yaw, piece.roll);
    }
  }

  // ── Lifecycle ────────────────────────────────────────────────────────────

  /** The empty van is far behind the runner. */
  private gone(ride: BusRide): boolean {
    return ride.phase === BusPhase.Done && ride.pose.distance < this.playerDistance() - RELEASE_BEHIND;
  }

  private create(ride: BusRide): Entry {
    const visual = this.pool.acquire(ASSET, null);
    const nodes = new Map<string, RigNode>();
    for (const node of visual.root.getChildTransformNodes(false)) {
      const name = node.name.slice(node.name.lastIndexOf(':') + 1);
      if (name.startsWith('VEH_BUS_A_PIVOT')) nodes.set(name, { node, position: node.position.clone(), rotation: node.rotationQuaternion?.clone() ?? null });
    }
    const wheels = ['FL', 'FR', 'RL', 'RR'].flatMap((key) => {
      const rig = nodes.get(NODE.wheel + key);
      return rig ? [{ rig, front: key.startsWith('F') }] : [];
    });
    const cargo: RigNode[] = [];
    for (let k = 1; nodes.has(NODE.cargo + k); k++) cargo.push(nodes.get(NODE.cargo + k)!);
    return {
      ride,
      visual,
      body: nodes.get(NODE.body) ?? null,
      wheels,
      cargo,
      roll: { value: 0, velocity: 0 },
      pitch: { value: 0, velocity: 0 },
      heave: { value: 0, velocity: 0 },
      lateral: 0,
      forward: ride.pose.speed,
      yaw: 0,
      steer: 0,
      airPitch: 0,
    };
  }

  private release(): void {
    const entry = this.entry;
    if (!entry) return;
    for (const rig of entry.cargo) rig.node.setEnabled(true);
    for (const rig of [entry.body, ...entry.wheels.map((w) => w.rig)]) {
      if (!rig) continue;
      rig.node.position.copyFrom(rig.position);
      rig.node.rotationQuaternion = rig.rotation?.clone() ?? null;
    }
    entry.visual.root.rotation.set(0, 0, 0);
    entry.visual.root.setEnabled(true);
    this.pool.release(entry.visual);
    this.entry = null;
  }
}
